import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Pages.css';
import canamLogo from '../images/CanAm Logo.png';
import { useApp } from '../context/AppContext';
import AddToCalendar from '../components/AddToCalendar';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const HOSTS = [
  { name: 'Alex', display: '(716) 908-0639', digits: '+17169080639' },
  { name: 'Pam',  display: '(716) 445-3007', digits: '+17164453007' },
];

const InvitePage = () => {
  const { settings } = useApp();
  const { eventDate, eventEndDate, rsvpDeadline, enforceDeadline, schedule, highlights, eventDescription, eventLocation, attire } = settings;
  const [timeLeft, setTimeLeft] = useState({});
  const [msgName, setMsgName] = useState('');
  const [msgText, setMsgText] = useState('');
  const [msgStatus, setMsgStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setMsgStatus('sending');
    try {
      const res = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: msgName, message: msgText }),
      });
      if (!res.ok) throw new Error();
      setMsgStatus('sent');
      setMsgName('');
      setMsgText('');
    } catch {
      setMsgStatus('error');
    }
  };

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = eventDate - now;
      if (diff <= 0) {
        setTimeLeft({ expired: true, ended: now >= eventEndDate });
        return;
      }
      setTimeLeft({
        days:    Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours:   Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [eventDate, eventEndDate]);

  const rsvpClosed = enforceDeadline && new Date() > rsvpDeadline;
  const deadlineDaysBefore = Math.round((eventDate - rsvpDeadline) / (1000 * 60 * 60 * 24));

  return (
    <div className="page-container invite-bg-wrap">
      <div className="invite-bg-logo-container" aria-hidden="true">
        <img src={canamLogo} alt="" className="invite-bg-logo" />
        <img src={canamLogo} alt="" className="invite-bg-logo-reflect" />
      </div>
      <div className="page-content">
        <div className="invite-hero">
          <img src={canamLogo} alt="CanAm Logo" className="invite-hero-logo" />
          <div>
            <h2>Invite & Event Details</h2>
            <p className="event-description">{eventDescription}</p>
          </div>
        </div>

        {/* Countdown */}
        <div className="info-section countdown-section">
          <h3>⏳ Time Until the Event</h3>
          {timeLeft.expired ? (
            timeLeft.ended
              ? <p className="event-description">Thanks for a great event — see you next year!</p>
              : <p className="event-description">The event is happening now — see you there!</p>
          ) : (
            <div className="countdown-grid">
              <div className="countdown-unit">
                <span className="countdown-number">{timeLeft.days ?? '--'}</span>
                <span className="countdown-label">Days</span>
              </div>
              <div className="countdown-unit">
                <span className="countdown-number">{timeLeft.hours ?? '--'}</span>
                <span className="countdown-label">Hours</span>
              </div>
              <div className="countdown-unit">
                <span className="countdown-number">{timeLeft.minutes ?? '--'}</span>
                <span className="countdown-label">Minutes</span>
              </div>
              <div className="countdown-unit">
                <span className="countdown-number">{timeLeft.seconds ?? '--'}</span>
                <span className="countdown-label">Seconds</span>
              </div>
            </div>
          )}
        </div>

        <div className="info-section">
          <h3>📅 Event Date & Time</h3>
          <div className="detail-item">
            <strong>Date:</strong> {eventDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          <div className="detail-item">
            <strong>Time:</strong> {eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </div>
          <div className="detail-item">
            <strong>Location:</strong>{' '}
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eventLocation)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="location-link"
            >
              {eventLocation}
            </a>
          </div>
          {attire && (
            <div className="detail-item">
              <strong>Attire:</strong> {attire}
            </div>
          )}
          <AddToCalendar />
        </div>

        {highlights && highlights.length > 0 && (
          <div className="info-section">
            <h3>🎯 What to Expect</h3>
            <ul className="event-highlights">
              {highlights.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
        )}

        {schedule && schedule.length > 0 && (
          <div className="info-section">
            <h3>🗓 Event Schedule</h3>
            <div className="schedule-list">
              {schedule.map(item => (
                <div key={item.id} className="schedule-item">
                  <span className="schedule-time">{item.time}</span>
                  <span className="schedule-event">{item.description}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="info-section">
          <h3>📋 RSVP Deadline</h3>
          <div className="detail-item">
            <strong>Please RSVP by:</strong> {rsvpDeadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          {deadlineDaysBefore > 0 && (
            <div className="detail-item" style={{ color: '#888', fontSize: '0.95rem' }}>
              That's {deadlineDaysBefore} day{deadlineDaysBefore !== 1 ? 's' : ''} before the event — so we can plan accordingly!
            </div>
          )}
        </div>

        <div className="info-section">
          <h3>💬 Message the Host</h3>
          <p className="event-description" style={{ marginBottom: '1rem' }}>
            Have a question before the event? Send us a message and we'll get back to you.
          </p>
          {msgStatus === 'sent' ? (
            <div className="msg-success">
              Message sent! We'll be in touch.
              <button className="msg-reset-btn" onClick={() => setMsgStatus(null)}>Send another</button>
            </div>
          ) : (
            <form className="msg-form" onSubmit={handleSendMessage}>
              <div className="form-group">
                <label htmlFor="msg-name">Your Name</label>
                <input
                  id="msg-name"
                  type="text"
                  className="msg-input"
                  placeholder="First Last"
                  value={msgName}
                  onChange={e => setMsgName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="msg-text">Message</label>
                <textarea
                  id="msg-text"
                  className="msg-input msg-textarea"
                  placeholder="What's on your mind?"
                  value={msgText}
                  onChange={e => setMsgText(e.target.value)}
                  rows={4}
                  required
                />
              </div>
              {msgStatus === 'error' && (
                <p className="msg-error">Something went wrong — please try again.</p>
              )}
              <button type="submit" className="msg-submit-btn" disabled={msgStatus === 'sending'}>
                {msgStatus === 'sending' ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          )}
          <div className="msg-alt-contact">
            <p className="msg-alt-contact-intro">Prefer to reach out directly?</p>
            {HOSTS.map(h => (
              <p key={h.digits} className="msg-alt-contact-row">
                <strong>{h.name}</strong> — {h.display}
                <a href={`sms:${h.digits}`} className="msg-alt-link">Text</a>
                <span className="msg-alt-sep">·</span>
                <a href={`tel:${h.digits}`} className="msg-alt-link">Call</a>
              </p>
            ))}
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <a href="/contact.vcf" className="save-contact-btn">📇 Save “CanAm Games” to your contacts</a>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#999' }}>
                So you'll recognize our event texts from (716) 249-3421.
              </p>
            </div>
          </div>
        </div>

        {rsvpClosed ? (
          <div className="cta-section" style={{ opacity: 0.55, cursor: 'default', pointerEvents: 'none' }}>
            <p>RSVPs are now closed — we'll see you at the event!</p>
          </div>
        ) : (
          <Link to="/rsvp" className="cta-section">
            <p>Ready to join? Head to the RSVP tab to let us know you're coming!</p>
          </Link>
        )}
      </div>
    </div>
  );
};

export default InvitePage;
